/**
 * The alert types (PLAN §11.1's alarm list, D27; phase 7b item 4): one id per
 * thing a tenant can be told about, what kind of thing it is, and its default
 * rule. The single list the contract, the evaluator, the settings screen and
 * the vendor dashboard draw from.
 *
 *   - A **condition** is a state that holds for a while (a node offline, a
 *     certificate expiring): it is raised when it starts to hold, resolved
 *     when it stops, and both are notified (`state.ts`).
 *   - An **event** happens at an instant (a failover, a break-glass sign-in):
 *     it is notified once, deduplicated by the cooldown, and never "resolved".
 *
 * Ids are never renamed: they are stored in every tenant's settings and in the
 * alert history.
 */

export const CONDITION_TYPES = ['server-offline-live', 'standby-missing', 'cert-expiring', 'clock-drift', 'device-disconnected-on-air', 'standby-out-of-sync'] as const;
export const EVENT_TYPES = ['failover', 'break-glass', 'cloud-production-failed', 'cloud-production-relaunched', 'forced-promotion'] as const;
export const ALERT_TYPES = [...CONDITION_TYPES, ...EVENT_TYPES] as const;

export type ConditionType = (typeof CONDITION_TYPES)[number];
export type EventType = (typeof EVENT_TYPES)[number];
export type AlertType = (typeof ALERT_TYPES)[number];

export const SEVERITIES = ['info', 'warning', 'critical'] as const;
export type Severity = (typeof SEVERITIES)[number];

/** Severity order: a channel's `minSeverity` admits this severity and every one above it. */
export function severityRank(severity: Severity): number {
  return SEVERITIES.indexOf(severity);
}

export type AlertTypeInfo = {
  readonly type: AlertType;
  readonly kind: 'condition' | 'event';
  readonly label: string;
  readonly description: string;
  /** The rule a tenant starts with: every type on, at this severity. */
  readonly defaultSeverity: Severity;
};

export const ALERT_CATALOG: Readonly<Record<AlertType, AlertTypeInfo>> = {
  'server-offline-live': {
    type: 'server-offline-live',
    kind: 'condition',
    label: 'Server offline during a live production',
    description: 'A node running a production that is on air has not been heard from for 90 seconds (three missed health reports).',
    defaultSeverity: 'critical',
  },
  'standby-missing': {
    type: 'standby-missing',
    kind: 'condition',
    label: 'Standby missing',
    description: 'A production of a main/backup pair runs unprotected: no standby follows it, so a failure of its node cannot fail over.',
    defaultSeverity: 'warning',
  },
  'cert-expiring': {
    type: 'cert-expiring',
    kind: 'condition',
    label: 'Certificate expiring',
    description: 'A node’s local HTTPS certificate expires within 14 days (it is renewed automatically; this means renewal is not reaching it).',
    defaultSeverity: 'warning',
  },
  'clock-drift': {
    type: 'clock-drift',
    kind: 'condition',
    label: 'Clock drift',
    description: 'A node’s clock is more than 50 ms away from the cloud’s. Timed takes and failover need NTP (D21).',
    defaultSeverity: 'warning',
  },
  'device-disconnected-on-air': {
    type: 'device-disconnected-on-air',
    kind: 'condition',
    label: 'Device disconnected on air',
    description: 'A device of a production that is on air has lost its connection.',
    defaultSeverity: 'critical',
  },
  'standby-out-of-sync': {
    type: 'standby-out-of-sync',
    kind: 'condition',
    label: 'Standby out of sync',
    description:
      'A production’s standby follows its active but has stopped keeping up — its acknowledgements stopped for 10 seconds or more — so a failure of the active node would not fail over to a current state.',
    defaultSeverity: 'warning',
  },
  failover: {
    type: 'failover',
    kind: 'event',
    label: 'Failover occurred',
    description: 'A production’s standby took over (its failover term went up) — automatically, or by an operator’s switchover.',
    defaultSeverity: 'warning',
  },
  'break-glass': {
    type: 'break-glass',
    kind: 'event',
    label: 'Break-glass sign-in used',
    description: 'Someone signed in to a node with an offline PIN or the site emergency account while it could not reach the cloud (D20).',
    defaultSeverity: 'warning',
  },
  'cloud-production-failed': {
    type: 'cloud-production-failed',
    kind: 'event',
    label: 'Cloud production failed',
    description: 'A cloud production could not be launched or kept running, and was stopped.',
    defaultSeverity: 'critical',
  },
  'cloud-production-relaunched': {
    type: 'cloud-production-relaunched',
    kind: 'event',
    label: 'Cloud production relaunched',
    description: 'A cloud production’s task stopped unexpectedly and was launched again.',
    defaultSeverity: 'warning',
  },
  'forced-promotion': {
    type: 'forced-promotion',
    kind: 'event',
    label: 'Standby forced on air',
    description:
      'An operator forced a production’s standby on air while its active node and every witness were unreachable (emergency activation). If the old active is still alive it may drive the devices until it sees the new term.',
    defaultSeverity: 'critical',
  },
};

export function isConditionType(type: AlertType): type is ConditionType {
  return ALERT_CATALOG[type].kind === 'condition';
}
